import { useGetAccounts } from "@/services/queries/accounts";
import { useGetCurrencies } from "@/services/queries/currency";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Step } from "./constants";
import InitialStep from "./initial-step";
import CurrencyStep from "./currency-step";
import NoteStep from "./note-step";
import AmountStep from "./amount-step";
import ExchangeRateStep from "./exchange-rate";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import FromAccountStep from "./from-account-step";
import ToAccountStep from "./to-account-step";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CreateTransferPayload,
  UpdateTransferPayload,
  useCreateTransactionAttachment,
  useCreateTransfer,
  useDeleteTransactionAttachment,
  useUpdateTransfer,
} from "@/services/mutations/transactions";
import { Account, ApiResponse, Transfer } from "@/lib/types";
import { toast } from "@/hooks/use-toast";
import { useLocation, useNavigate } from "react-router-dom";
import useVessel from "@/store/use-vessel";
import { useGetVessels } from "@/services/queries/vessel";
import { formatDate, toDateOnlyString } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useConversionRate } from "@/hooks/use-conversion-rate";
import { Mixpanel } from "@/lib/mixpanel";
import { set } from "date-fns";
import { api, BASE_URL } from "@/lib/api";

export type TransferConversionRateField =
  | "fromAccountConversionRate"
  | "toAccountConversionRate";

export type TransferFormValues = {
  transactionDate: Date;
  amount: number;
  description: string;
  currencyId: string;
  fromAccountId: string;
  toAccountId: string;
  fromAccountConversionRate?: number;
  toAccountConversionRate?: number;
  files: { id: string; url: string }[] | File[];
};

const TransferForm = ({
  title,
  open,
  setOpen,
  defaultValues,
  refetch,
  defaultDate,
}: {
  title?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultValues?: Partial<Transfer>;
  refetch?: () => void;
  defaultDate?: Date;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [step, setStep] = useState<number>(Step.INITAL);
  const [conversionRateField, setConversionRateField] =
    useState<TransferConversionRateField>("toAccountConversionRate");
  const { t } = useTranslation();
  const { setSelectedVessel } = useVessel();
  const { data: currenciesData, isPending: isCurrenciesPending } =
    useGetCurrencies();
  const { data: accountsData, isPending: isAccountsPending } = useGetAccounts();
  const { data: vesselsData } = useGetVessels();
  const { mutateAsync: createTransactionAttachment } =
    useCreateTransactionAttachment();
  const { mutateAsync: deleteTransactionAttachment } =
    useDeleteTransactionAttachment();

  // Hard guard against double-submit: a synchronous ref blocks duplicate requests even when rapid taps fire before React re-renders. Cleared only when the whole flow settles.
  const isSubmittingRef = useRef(false);

  const { mutate: createTransfer, isPending: isCreatingTransfer } =
    useCreateTransfer({
      onSuccess: async (data) => {
        if (form.getValues().files && form.getValues().files.length > 0) {
          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // transfer was already created, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled(
            form.getValues().files.map((file) =>
              createTransactionAttachment({
                transactionId: data.value.id,
                file: file as File,
              })
            )
          );
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }
        }

        setOpen(false);
        toast({
          title: t("success_message.title"),
          description: t("success_message.add_transfer_description"),
        });

        const toAccountVessel = vesselsData?.value?.find(
          (vessel) =>
            vessel.id ===
            accountsData?.value?.find(
              (account) => account.id === form.getValues("toAccountId")
            )?.vesselId
        );

        if (toAccountVessel) {
          setSelectedVessel(toAccountVessel);
        } else {
          const fromAccountVessel = vesselsData?.value?.find(
            (vessel) =>
              vessel.id ===
              accountsData?.value?.find(
                (account) => account.id === form.getValues("fromAccountId")
              )?.vesselId
          );

          if (fromAccountVessel) {
            setSelectedVessel(fromAccountVessel);
          }
        }

        queryClient.invalidateQueries({
          queryKey: ["transactions-by-date-range"],
        });

        navigate(
          `${pathname}?date=${formatDate(form.getValues("transactionDate"), "yyyy-MM-dd")}`
        );

        form.reset();
        isSubmittingRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        isSubmittingRef.current = false;
      },
    });
  const { mutate: updateTransfer, isPending: isUpdatingTransfer } =
    useUpdateTransfer({
      onSuccess: async (data) => {
        if (form.getValues("files") && form.getValues("files").length > 0) {
          const deletedAttachments =
            defaultValues && "attachments" in defaultValues
              ? defaultValues?.attachments?.filter(
                  (attachment) =>
                    !form
                      .watch("files")
                      .some(
                        (file) =>
                          (file as unknown as { id: string }).id ===
                          (attachment as unknown as { id: string }).id
                      )
                )
              : [];

          const newAttachments = form
            .watch("files")
            .filter((file) => file instanceof File);

          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // transfer was already created, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled([
            ...newAttachments.map((file) =>
              createTransactionAttachment({
                file,
                transactionId: data.value.id,
              })
            ),
            ...(deletedAttachments ?? []).map((attachment) =>
              deleteTransactionAttachment({
                transactionId: data.value.id,
                attachmentId: (attachment as { id: string }).id,
              })
            ),
          ]);
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }

          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_transfer_description"),
          });

          refetch?.();
          setOpen(false);

          Mixpanel.track("Transfer Edited Successfully");
        } else {
          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_transfer_description"),
          });

          refetch?.();
          setOpen(false);

          Mixpanel.track("Transfer Edited Successfully");
        }

        isSubmittingRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        isSubmittingRef.current = false;
      },
    });
  const [fromAccount, setFromAccount] = useState<Account | null>(null);
  const [toAccount, setToAccount] = useState<Account | null>(null);

  const transferFormSchema = z
    .object({
      amount: z.number().min(1),
      currencyId: z.string().min(1),
      fromAccountId: z.string().min(1),
      toAccountId: z.string().min(1),
      transactionDate: z.date(),
      description: z.string().optional(),
      fromAccountConversionRate: z.number().or(z.undefined()),
      toAccountConversionRate: z.number().or(z.undefined()),
      files: z.array(z.any()).optional(),
    })
    .superRefine((data, ctx) => {
      if (
        fromAccount &&
        data.currencyId !== fromAccount.currencyId &&
        data.fromAccountConversionRate === undefined
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Exchange rate is required",
          path: ["fromAccountConversionRate"],
        });
      }

      if (
        toAccount &&
        data.currencyId !== toAccount?.currencyId &&
        data.toAccountConversionRate === undefined
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Exchange rate is required",
          path: ["toAccountConversionRate"],
        });
      }
    });

  const form = useForm<TransferFormValues>({
    resolver: zodResolver(transferFormSchema),
    defaultValues: {
      transactionDate: defaultValues?.transactionDate
        ? new Date(defaultValues.transactionDate)
        : new Date(),
      amount: defaultValues?.amount ?? undefined,
      description: defaultValues?.description ?? "",
      currencyId: defaultValues?.currencyId ?? "",
      fromAccountId: defaultValues?.fromAccountId ?? "",
      toAccountId: defaultValues?.toAccountId ?? "",
      fromAccountConversionRate: defaultValues?.ledgerEntries?.find(
        (entry) => entry.accountId === defaultValues.fromAccountId
      )?.conversionRate,
      toAccountConversionRate: defaultValues?.ledgerEntries?.find(
        (entry) => entry.accountId === defaultValues.toAccountId
      )?.conversionRate,
      files: [],
    },
  });

  const watchedCurrencyId = form.watch("currencyId");
  const watchedTransactionDate = form.watch("transactionDate");
  const transactionDateStr = watchedTransactionDate
    ? toDateOnlyString(watchedTransactionDate)
    : undefined;

  const {
    rate: fromAccountConversionRate,
    setRate: setFromAccountConversionRate,
  } = useConversionRate({
    fromCurrencyId: watchedCurrencyId,
    toCurrencyId: fromAccount?.currencyId,
    date: transactionDateStr,
    initialRate: defaultValues?.ledgerEntries?.find(
      (entry) => entry.accountId === defaultValues.fromAccountId
    )?.conversionRate,
  });
  const { rate: toAccountConversionRate, setRate: setToAccountConversionRate } =
    useConversionRate({
      fromCurrencyId: watchedCurrencyId,
      toCurrencyId: toAccount?.currencyId,
      date: transactionDateStr,
      initialRate: defaultValues?.ledgerEntries?.find(
        (entry) => entry.accountId === defaultValues.toAccountId
      )?.conversionRate,
    });

  // Sync derived rates to RHF for submit & zod validation.
  useEffect(() => {
    form.setValue("fromAccountConversionRate", fromAccountConversionRate);
  }, [fromAccountConversionRate, form]);
  useEffect(() => {
    form.setValue("toAccountConversionRate", toAccountConversionRate);
  }, [toAccountConversionRate, form]);

  const initialValues = useMemo(() => {
    if (!defaultValues) return undefined;

    return {
      transactionDate: defaultValues?.transactionDate
        ? new Date(defaultValues.transactionDate)
        : new Date(),
      amount: defaultValues?.amount ?? undefined,
      description: defaultValues?.description ?? "",
      currencyId: defaultValues?.currencyId ?? "",
      fromAccountId: defaultValues?.fromAccountId ?? "",
      toAccountId: defaultValues?.toAccountId ?? "",
      version: defaultValues?.version ?? 0,
      fromAccountConversionRate: defaultValues?.ledgerEntries?.find(
        (entry) => entry.accountId === defaultValues.fromAccountId
      )?.conversionRate,
      toAccountConversionRate: defaultValues?.ledgerEntries?.find(
        (entry) => entry.accountId === defaultValues.toAccountId
      )?.conversionRate,
    };
  }, [defaultValues]);

  useEffect(() => {
    const getPreviewUrls = async () => {
      const files =
        defaultValues && "attachments" in defaultValues
          ? (defaultValues?.attachments ?? [])
          : [];

      if (!files || files.length === 0) {
        return;
      }

      try {
        const urlResults = await Promise.all(
          files.map(async (file) => {
            const res = await api.get<ApiResponse<string>>(
              `${BASE_URL}/transactions/attachments/${file.id}/url`
            );
            return { id: file.id, url: res.data.value };
          })
        );
        form.setValue("files", urlResults);
      } catch (error) {
        console.error("Error fetching attachment URLs:", error);
      }
    };

    getPreviewUrls();
  }, [defaultValues, form, open]);

  const currencies = useMemo(() => {
    return currenciesData?.value ?? [];
  }, [currenciesData?.value]);

  const accounts = useMemo(() => {
    return accountsData?.value ?? [];
  }, [accountsData?.value]);

  const getStep = () => {
    switch (step) {
      case Step.INITAL:
        return (
          <InitialStep
            title={title}
            form={form}
            setStep={setStep}
            currencies={currencies}
            accounts={accounts}
            toAccount={toAccount}
            fromAccount={fromAccount}
            setConversionRateField={setConversionRateField}
            fromAccountConversionRate={fromAccountConversionRate}
            toAccountConversionRate={toAccountConversionRate}
            isSubmitting={isCreatingTransfer || isUpdatingTransfer}
          />
        );
      case Step.CURRENCY:
        return (
          <CurrencyStep
            isCurrenciesPending={isCurrenciesPending}
            currencies={currencies}
            form={form}
            setStep={setStep}
          />
        );
      case Step.NOTE:
        return <NoteStep form={form} setStep={setStep} />;
      case Step.FROM_ACCOUNT:
        return (
          <FromAccountStep
            form={form}
            setStep={setStep}
            accounts={accounts}
            isPending={isAccountsPending}
            currencies={currencies}
          />
        );
      case Step.TO_ACCOUNT:
        return (
          <ToAccountStep
            form={form}
            setStep={setStep}
            accounts={accounts}
            isPending={isAccountsPending}
            currencies={currencies}
          />
        );
      case Step.AMOUNT:
        return <AmountStep form={form} setStep={setStep} />;
      case Step.EXCHANGE_RATE:
        return (
          <ExchangeRateStep
            setStep={setStep}
            fromCurrency={currencies.find(
              (c) => c.id === form.watch("currencyId")
            )}
            toCurrency={currencies.find(
              (c) =>
                c.id ===
                (conversionRateField === "fromAccountConversionRate"
                  ? fromAccount?.currencyId
                  : toAccount?.currencyId)
            )}
            initialRate={
              conversionRateField === "fromAccountConversionRate"
                ? fromAccountConversionRate
                : toAccountConversionRate
            }
            onSave={
              conversionRateField === "fromAccountConversionRate"
                ? setFromAccountConversionRate
                : setToAccountConversionRate
            }
          />
        );
      default:
        return null;
    }
  };

  const onSubmit = (values: TransferFormValues) => {
    // Block re-entry while a submission is already in flight.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const vesselId =
      toAccount?.vesselId ?? fromAccount?.vesselId ?? defaultValues?.vesselId;

    const payload: CreateTransferPayload = {
      transactionDate: values.transactionDate,
      amount: values.amount,
      description: values.description,
      currencyId: values.currencyId,
      vesselId: vesselId ?? "",
      fromAccountId: values.fromAccountId,
      toAccountId: values.toAccountId,
      fromAccountConversionRate: values.fromAccountConversionRate,
      toAccountConversionRate: values.toAccountConversionRate,
    };

    if (defaultValues) {
      handleEditTransfer({
        ...payload,
        id: defaultValues.id ?? "",
        version: defaultValues.version ?? 0,
      });
    } else {
      handleAddTransfer(payload);
    }
  };

  const handleAddTransfer = (values: CreateTransferPayload) => {
    createTransfer(values);
  };

  const handleEditTransfer = (values: UpdateTransferPayload) => {
    updateTransfer(values);
  };

  useEffect(() => {
    if (initialValues) {
      form.reset(initialValues);
    }
  }, [form, initialValues]);

  // New transfers start on the day picked in the header; the form stays
  // mounted while closed, so its mount-time default would go stale. Keep the
  // current time: transfers send the full instant, and local midnight is the
  // previous day in UTC east of Greenwich.
  useEffect(() => {
    if (open && !defaultValues) {
      const now = new Date();
      form.setValue(
        "transactionDate",
        defaultDate
          ? set(defaultDate, {
              hours: now.getHours(),
              minutes: now.getMinutes(),
              seconds: now.getSeconds(),
            })
          : now
      );
    }
  }, [open, defaultValues, defaultDate, form]);

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.toAccountId) {
        const account = accounts.find(
          (account) => account.id === value.toAccountId
        );
        if (account) {
          setToAccount(account);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, accounts]);

  useEffect(() => {
    if (defaultValues && accounts.length > 0) {
      const fromAccount = accounts.find(
        (account) => account.id === defaultValues.fromAccountId
      );
      if (fromAccount) {
        setFromAccount(fromAccount);
      }

      const toAccount = accounts.find(
        (account) => account.id === defaultValues.toAccountId
      );
      if (toAccount) {
        setToAccount(toAccount);
      }
    }
  }, [defaultValues, form, accounts]);

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.fromAccountId) {
        const account = accounts.find(
          (account) => account.id === value.fromAccountId
        );
        if (account) {
          setFromAccount(account);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, accounts]);

  return (
    <Drawer
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          setStep(Step.INITAL);
          form.reset(initialValues ?? {});
        }
      }}
    >
      <DrawerContent
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        data-testid="transfer-form-drawer"
      >
        <DrawerHeader className="hidden">
          <DrawerTitle>{title ?? t("title.add_transfer")}</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription />
        <Form {...form}>
          <form
            data-testid="transfer-form"
            onSubmit={(e) => {
              form.handleSubmit(onSubmit, (errors) => {
                console.warn(errors);
              })(e);
            }}
          >
            {getStep()}
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

export default TransferForm;
